import { FC, useState } from 'react';
import styled from '@emotion/styled';
import { Chart } from 'react-chartjs-2';
import {
  Typography,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
} from '@material-ui/core';

import { PaperTitle, PaperTitleText, PaperContent } from '../../primitive/Paper';
import { LinkPrefetchReportData } from '../../../../hooks/useLinkPrefetchReport';
import HelpIcon from '@material-ui/icons/Help';

interface MetricData {
  label: string;
  before: string;
  after: string;
  improvement: number;
}

type Props = {
  reportData: LinkPrefetchReportData;
  thresholdParam?: number;
  debugReportParam?: boolean;
};

const SITE_WIDE_LABEL = 'サイト全体平均';

const ReportChart: FC<Props> = ({ reportData, thresholdParam, debugReportParam }) => {
  const [targetSelect, setTargetSelect] = useState(SITE_WIDE_LABEL);
  const [activeMetric, setActiveMetric] = useState('DCL');
  const pvThreshold = thresholdParam || 1000;

  // reportData.patterns のキーを PV 降順に抽出 (サイト全体平均の "" は除外)
  const patterns = Object.keys(reportData.patterns || {})
    .filter((pattern) => pattern != '')
    .sort((a, b) => reportData.patterns[b].experimental.pv - reportData.patterns[a].experimental.pv);
  const selectedPattern = targetSelect !== SITE_WIDE_LABEL ? targetSelect : '';
  const pattern = reportData.patterns[selectedPattern];
  if (!pattern) {
    if (targetSelect !== SITE_WIDE_LABEL) {
      setTargetSelect(SITE_WIDE_LABEL);
    }
    return <div>データが存在しません</div>;
  }

  // レスポンスデータからチャートデータを生成
  const generateChartData = () => {
    // ラベルを生成（MM/DD形式）
    const labels = pattern.dailies.map((daily) => {
      const [, month, day] = daily.date.split('-');
      return `${parseInt(month, 10)}/${parseInt(day, 10)}`;
    });

    const datasets = [];
    if (activeMetric === 'DCL' || activeMetric === '両方表示') {
      const dclTestData = pattern.dailies.map((daily) => daily.experimental.dcl / 1000);
      const dclControlData = pattern.dailies.map((daily) => daily.control.dcl / 1000);
      datasets.push(
        {
          label: 'DCL 設定グループ (90%)',
          data: dclTestData,
          borderColor: '#6366f1',
          pointRadius: 4,
          pointBackgroundColor: '#6366f1',
          backgroundColor: 'transparent',
        },
        {
          label: 'DCL 未設定グループ (10%)',
          data: dclControlData,
          borderColor: '#9ca3af',
          pointRadius: 4,
          pointBackgroundColor: '#9ca3af',
          backgroundColor: 'transparent',
          borderDash: [6, 6],
        },
      );
    }
    if (activeMetric === 'LCP' || activeMetric === '両方表示') {
      const lcpTestData = pattern.dailies.map((daily) => daily.experimental.lcp / 1000);
      const lcpControlData = pattern.dailies.map((daily) => daily.control.lcp / 1000);
      datasets.push(
        {
          label: 'LCP 設定グループ (90%)',
          data: lcpTestData,
          borderColor: '#a855f7',
          pointRadius: 4,
          pointBackgroundColor: '#a855f7',
          backgroundColor: 'transparent',
        },
        {
          label: 'LCP 未設定グループ (10%)',
          data: lcpControlData,
          borderColor: '#c4b5fd',
          pointRadius: 4,
          pointBackgroundColor: '#c4b5fd',
          backgroundColor: 'transparent',
          borderDash: [6, 6],
        },
      );
    }

    return {
      labels,
      datasets,
    };
  };
  const chartData = generateChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
      },
    },
    scales: {
      y: {
        min: 0.0,
        ticks: {
          callback: function (value: any) {
            return value.toFixed(2) + '秒';
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const metricsData: MetricData[] = (() => {
    const calculateImprovement = (before: number, after: number) => {
      if (before === 0) {
        return 0;
      }
      // DCL, LCP, CLS は値が小さいほど良いため、 (before - after) で改善度を計算
      return ((before - after) / before) * 100;
    };

    const dclImprovement = calculateImprovement(pattern.control.dcl, pattern.experimental.dcl);
    const lcpImprovement = calculateImprovement(pattern.control.lcp, pattern.experimental.lcp);
    const clsImprovement = calculateImprovement(pattern.control.cls, pattern.experimental.cls);

    return [
      {
        label: 'DOM Content Loaded (DCL)',
        before: `${(pattern.control.dcl / 1000).toFixed(2)}秒`,
        after: `${(pattern.experimental.dcl / 1000).toFixed(2)}秒`,
        improvement: Math.round(dclImprovement),
      },
      {
        label: 'Largest Contentful Paint (LCP)',
        before: `${(pattern.control.lcp / 1000).toFixed(2)}秒`,
        after: `${(pattern.experimental.lcp / 1000).toFixed(2)}秒`,
        improvement: Math.round(lcpImprovement),
      },
      {
        label: 'Cumulative Layout Shift (CLS)',
        before: (pattern.control.cls / 1000).toFixed(2),
        after: (pattern.experimental.cls / 1000).toFixed(2),
        improvement: Math.round(clsImprovement),
      },
    ];
  })();

  return (
    <Container>
      <PaperTitle>
        <TitleWithButtons>
          <PaperTitleText>推移グラフ</PaperTitleText>
          <RightSection>
            <FormControl size="small" style={{ minWidth: 150, marginRight: 16 }}>
              <InputLabel>表示対象</InputLabel>
              <Select value={targetSelect} onChange={(e) => setTargetSelect(e.target.value as string)}>
                <MenuItem value={SITE_WIDE_LABEL}>{SITE_WIDE_LABEL}</MenuItem>
                {patterns.map((pattern) => (
                  <MenuItem key={pattern} value={pattern}>
                    {pattern.length > 30 ? pattern.slice(0, 30) + '...' : pattern}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {debugReportParam && (
              <MetricButtons>
                {['DCL', 'LCP', '両方表示'].map((metric) => (
                  <MetricButton
                    key={metric}
                    variant={activeMetric === metric ? 'outlined' : 'contained'}
                    size="small"
                    onClick={() => setActiveMetric(metric)}
                  >
                    {metric}
                  </MetricButton>
                ))}
              </MetricButtons>
            )}
          </RightSection>
        </TitleWithButtons>
      </PaperTitle>
      <PaperContent>
        <ChartTopLeftNote>
          <Typography variant="body2" color="textSecondary">
            {targetSelect}の読み込み時間
          </Typography>
        </ChartTopLeftNote>
        <ChartWrapper>
          <Chart type="line" data={chartData} options={chartOptions} />
        </ChartWrapper>
      </PaperContent>

      <MetricsContainer>
        {metricsData.map((metric, index) => (
          <MetricCard key={index}>
            <CardContent>
              <MetricLabel variant="body2">{metric.label}</MetricLabel>
              {!debugReportParam && index > 0 ? (
                <Tooltip title="有効PVが不足しています">
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '24px' }}>
                    <ImprovementChipInsufficient label="データ不足" />
                    <HelpIcon style={{ fontSize: 16 }} />
                  </div>
                </Tooltip>
              ) : (
                <>
                  <MetricValues>
                    <MetricValue variant="h6">{metric.before}</MetricValue>
                    <ArrowIcon>→</ArrowIcon>
                    <MetricValue variant="h6">{metric.after}</MetricValue>
                  </MetricValues>
                  {pattern.experimental.pv < pvThreshold ? (
                    <>
                      <Tooltip title="有効PVが不足しています">
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <ImprovementChipInsufficient label="データ不足" />
                          <HelpIcon style={{ fontSize: 16 }} />
                        </div>
                      </Tooltip>
                    </>
                  ) : metric.improvement > 0 ? (
                    <ImprovementChipBetter label={`${metric.improvement}%改善`} />
                  ) : metric.improvement < 0 ? (
                    <ImprovementChipWorse label={`${-metric.improvement}%悪化`} />
                  ) : (
                    <ImprovementChipNeutral label={`${metric.improvement}%改善`} />
                  )}
                </>
              )}
            </CardContent>
          </MetricCard>
        ))}
      </MetricsContainer>
    </Container>
  );
};

const Container = styled('div')({
  width: '100%',
});

const TitleWithButtons = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

const RightSection = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const MetricButtons = styled('div')({
  display: 'flex',
});

const MetricButton = styled(Button)<{ variant: 'contained' | 'outlined' }>(({ variant }) => ({
  minWidth: '60px',
  fontSize: '12px',
  ...(variant === 'outlined' && {
    backgroundColor: '#eeeeee',
  }),
}));

const ChartTopLeftNote = styled('div')({
  width: '100%',
  marginTop: 8,
});

const ChartWrapper = styled('div')({
  height: '420px',
  marginTop: '8px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e6e7e8',
  padding: '0 24px 24px 24px',
  borderRadius: '4px',
});

const MetricsContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
  marginTop: '24px',
  padding: '24px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e6e7e8',
  borderRadius: '4px',
});

const MetricCard = styled(Card)({
  textAlign: 'center',
});

const MetricLabel = styled(Typography)({
  marginBottom: '8px',
  fontWeight: 500,
});

const MetricValues = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginBottom: '8px',
});

const MetricValue = styled(Typography)({
  fontWeight: 600,
});

const ArrowIcon = styled('span')({
  color: '#666',
  fontSize: '18px',
});

const ImprovementChipBetter = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#E8F5E8',
  color: '#2E7D32',
});

const ImprovementChipWorse = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#FDECEA',
  color: '#C62828',
});

const ImprovementChipNeutral = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#FFF8E1',
  color: '#FF8F00',
});

const ImprovementChipInsufficient = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#F5F5F5',
  color: '#9E9E9E',
});

export default ReportChart;
